import { zodResolver } from '@hookform/resolvers/zod';
import { AppError, AppErrorCode } from '@repo/error';
import { t, validationError } from '@repo/i18n';
import { useLocale } from '@repo/i18n/react';
import type { ApiTokenType } from '@repo/shared/schema';
import { Button } from '@repo/ui/button';
import { Card, CardContent } from '@repo/ui/card';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@repo/ui/form';
import { useCopyToClipboard } from '@repo/ui/hooks/use-copy-to-clipboard';
import { Input } from '@repo/ui/input';
import { cn } from '@repo/ui/lib/utils';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@repo/ui/select';
import { Switch } from '@repo/ui/switch';
import { AnimatePresence, motion } from 'framer-motion';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { match } from 'ts-pattern';
import { z } from 'zod';
import { trpc } from '@/providers/trpc';
import { useOrganizationStore } from '@/stores/organization-store';

export const EXPIRATION_DATES = {
  ONE_WEEK: '7 days',
  ONE_MONTH: '1 month',
  THREE_MONTHS: '3 months',
  SIX_MONTHS: '6 months',
  ONE_YEAR: '12 months',
} as const;

const ZCreateTokenFormSchema = z.object({
  tokenName: z.string().min(3, {
    get message() {
      return t('auth.theTokenNameShouldBe3CharactersOrLonger');
    },
  }),
  expirationDate: z
    .enum(['ONE_WEEK', 'ONE_MONTH', 'THREE_MONTHS', 'SIX_MONTHS', 'ONE_YEAR'])
    .optional(),
});

type TCreateTokenFormSchema = z.infer<typeof ZCreateTokenFormSchema>;

type NewlyCreatedToken = {
  id: string;
  token: string;
};

export type ApiTokenFormProps = {
  className?: string;
  tokens?: Pick<ApiTokenType, 'id'>[];
};

export const ApiTokenForm = ({ className, tokens }: ApiTokenFormProps) => {
  useLocale();

  const [, copy] = useCopyToClipboard();

  const { currentOrganization } = useOrganizationStore();

  const [newlyCreatedToken, setNewlyCreatedToken] = useState<NewlyCreatedToken | null>();
  const [noExpirationDate, setNoExpirationDate] = useState(false);

  const { mutateAsync: createTokenMutation } = trpc.apiToken.createApiToken.useMutation({
    onSuccess(data) {
      setNewlyCreatedToken(data);
    },
  });

  const form = useForm<TCreateTokenFormSchema>({
    resolver: zodResolver(ZCreateTokenFormSchema, { error: validationError }),
    defaultValues: {
      tokenName: '',
      expirationDate: 'ONE_MONTH',
    },
  });

  const copyToken = async (token: string) => {
    try {
      const copied = await copy(token);

      if (!copied) {
        throw new Error(t('extra.unableToCopyTheToken'));
      }

      toast.success(t('auth.tokenCopiedToClipboard'), {
        get description() {
          return t('auth.theTokenWasCopiedToYourClipboard');
        },
      });
    } catch (error) {
      toast.error(t('workspace.anUnknownErrorOccurred'), {
        get description() {
          return t('auth.weWereUnableToCopyTheTokenToYour');
        },
      });
    }
  };

  const onSubmit = async ({ tokenName, expirationDate }: TCreateTokenFormSchema) => {
    try {
      await createTokenMutation({
        organizationId: currentOrganization?.id,
        tokenName,
        expiresIn: noExpirationDate ? undefined : expirationDate,
      });

      toast.success(t('auth.tokenCreated'), {
        get description() {
          return t('auth.aNewTokenWasCreatedSuccessfully');
        },
      });

      form.reset();
    } catch (err) {
      const error = AppError.parseError(err);

      const errorMessage = match(error.code)
        .with(AppErrorCode.UNAUTHORIZED, () => t('extra.youDoNotHavePermissionToCreateATokenFor'))
        .otherwise(() => t('extra.somethingWentWrongPleaseTryAgainLater'));

      toast.error(t('auth.anErrorOccurred'), {
        description: errorMessage,
      });
    }
  };

  return (
    <div className={cn(className)}>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <fieldset
            className="mt-6 flex w-full flex-col gap-4"
            disabled={form.formState.isSubmitting}
          >
            <FormField
              control={form.control}
              name="tokenName"
              render={({ field }) => (
                <FormItem className="flex-1">
                  <FormLabel className="text-muted-foreground">{t('auth.tokenName')}</FormLabel>

                  <div className="flex items-center gap-x-4">
                    <FormControl className="flex-1">
                      <Input type="text" {...field} />
                    </FormControl>
                  </div>

                  <FormDescription className="text-xs italic">
                    {t('auth.pleaseEnterAMeaningfulNameForYourTokenThis')}{' '}
                  </FormDescription>

                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex flex-col gap-4 md:flex-row">
              <FormField
                control={form.control}
                name="expirationDate"
                render={({ field }) => (
                  <FormItem className="flex-1">
                    <FormLabel className="text-muted-foreground">
                      {t('auth.tokenExpirationDate')}
                    </FormLabel>

                    <div className="flex items-center gap-x-4">
                      <FormControl className="flex-1">
                        <Select disabled={noExpirationDate} onValueChange={field.onChange}>
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder={t('auth.choose')} />
                          </SelectTrigger>
                          <SelectContent>
                            {Object.entries(EXPIRATION_DATES).map(([key, date]) => (
                              <SelectItem key={key} value={key}>
                                {date}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </FormControl>
                    </div>

                    <FormMessage />
                  </FormItem>
                )}
              />

              <div>
                <FormLabel className="mt-2 text-muted-foreground">
                  {t('auth.neverExpire')}
                </FormLabel>
                <div className="block md:py-1.5">
                  <Switch
                    checked={noExpirationDate}
                    className="mt-2 bg-background"
                    onCheckedChange={setNoExpirationDate}
                  />
                </div>
              </div>
            </div>

            <Button
              className="hidden md:inline-flex"
              loading={form.formState.isSubmitting}
              type="submit"
            >
              {t('auth.createToken')}{' '}
            </Button>

            <div className="md:hidden">
              <Button loading={form.formState.isSubmitting} type="submit">
                {t('auth.createToken')}{' '}
              </Button>
            </div>
          </fieldset>
        </form>
      </Form>

      <AnimatePresence>
        {newlyCreatedToken && tokens?.find((token) => token.id === newlyCreatedToken.id) && (
          <motion.div
            animate={{ opacity: 1, y: 0 }}
            className="mt-8"
            exit={{ opacity: 0, y: 40 }}
            initial={{ opacity: 0, y: -40 }}
          >
            <Card>
              <CardContent className="p-4">
                <p className="mt-2 text-muted-foreground text-sm">
                  {t('auth.yourTokenWasCreatedSuccessfullyMakeSureToCopy')}{' '}
                </p>

                <p className="my-4 rounded-md bg-muted-foreground/10 px-2.5 py-1 font-mono text-sm">
                  {newlyCreatedToken.token}
                </p>

                <Button onClick={() => void copyToken(newlyCreatedToken.token)} variant="outline">
                  {t('auth.copyToken')}{' '}
                </Button>
              </CardContent>
            </Card>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
